import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { products } from '@/utils/api';
import { Palette, Users, ShoppingBag, MessageCircle, Info, Heart } from 'lucide-react';
import { toast } from 'sonner';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [likedProducts, setLikedProducts] = useState(new Set());

  useEffect(() => {
    loadFeaturedProducts();
    // Load liked products from localStorage
    const saved = localStorage.getItem('likedProducts');
    if (saved) {
      setLikedProducts(new Set(JSON.parse(saved)));
    }
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      // First try to get admin-curated featured products
      const featuredResponse = await products.getFeatured();
      if (featuredResponse.data && featuredResponse.data.length > 0) {
        setFeaturedProducts(featuredResponse.data);
        return;
      }
      
      // Fallback to all products if no featured
      const response = await products.getAll();
      // Only show approved products with images
      const validProducts = response.data.filter(p => 
        p.status === 'approved' && 
        p.isActive !== false &&
        p.images && 
        p.images.length > 0
      );
      
      // Sort by creation date (most recent first) and take top 15
      const sortedProducts = validProducts.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // Most recent first
      });
      
      setFeaturedProducts(sortedProducts.slice(0, 15)); // Maximum 15 products
    } catch (error) {
      console.error('Error loading products:', error);
      setFeaturedProducts([]);
    }
  };

  const handleLike = async (e, productId) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    
    // Check if already liked
    if (likedProducts.has(productId)) {
      toast.info('You already loved this artwork!');
      return;
    }
    
    try {
      const response = await products.like(productId);
      
      // Update local state
      const newLiked = new Set(likedProducts);
      newLiked.add(productId);
      setLikedProducts(newLiked);
      localStorage.setItem('likedProducts', JSON.stringify([...newLiked]));
      
      // Update product likes in state
      setFeaturedProducts(prev => prev.map(p => 
        p.id === productId ? {...p, likes: response.data.likes} : p
      ));
      
      toast.success('Thank you for your appreciation!');
    } catch (error) {
      toast.error('Could not register your love. Try again!');
    }
  };
  return (
    <div data-testid="home-page">
      <SEO 
        title="Home"
        description="ARTCARDO - Discover unique handcrafted artworks from independent artists worldwide. Buy paintings, sculptures, crafts and more directly from creators."
        keywords="art marketplace, buy art online, handmade art, paintings, sculptures, Indian artists, contemporary art"
        url="/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "ARTCARDO",
          "url": "https://www.artcardo.com",
          "description": "Art & Craft Marketplace - Discover unique artworks from independent artists",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.artcardo.com/products?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <Navbar />
      
      <section className="hero" data-testid="hero-section">
        <div className="hero-content">
          <h1 data-testid="hero-title">Art That Speaks</h1>
          <p data-testid="hero-subtitle">
            Buy authentic art with confidence through our secure payment system. Simple, safe, and hassle-free!
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/products" data-testid="explore-btn">
              <Button className="btn btn-primary">
                Explore Art
              </Button>
            </Link>
            <Link to="/register" data-testid="become-seller-btn">
              <Button className="btn btn-secondary">
                Sell Your Art
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="text-center" data-testid="feature-quality">
            <div className="w-16 h-16 bg-[#000000] rounded-none flex items-center justify-center mx-auto mb-4 border-2 border-[#ef4444]">
              <Palette className="w-8 h-8 text-[#ef4444]" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Unique Artworks</h3>
            <p className="text-sm text-gray-600">
              Every piece is handcrafted with care and creativity by talented artists
            </p>
          </div>
          <div className="text-center" data-testid="feature-artists">
            <div className="w-16 h-16 bg-[#000000] rounded-none flex items-center justify-center mx-auto mb-4 border-2 border-[#ef4444]">
              <Users className="w-8 h-8 text-[#ef4444]" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Support Artists</h3>
            <p className="text-sm text-gray-600">
              Buy directly from creators and support their passion and livelihood
            </p>
          </div>
          <div className="text-center" data-testid="feature-secure">
            <div className="w-16 h-16 bg-[#000000] rounded-none flex items-center justify-center mx-auto mb-4 border-2 border-[#ef4444]">
              <ShoppingBag className="w-8 h-8 text-[#ef4444]" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Easy Purchase</h3>
            <p className="text-sm text-gray-600">
              Secure online checkout with multiple payment options
            </p>
          </div>
        </div>
      </section>

      <section className="section" style={{background: '#f5f5f5'}}>
        <h2 className="section-title" data-testid="featured-title">Featured Artworks</h2>
        
        {featuredProducts.length > 0 ? (
          <>
            <p className="section-subtitle" data-testid="featured-subtitle">
              Curated collection from our talented artists
            </p>
            <div className="products-grid" data-testid="featured-grid">
              {featuredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="product-card"
                  data-testid={`product-${product.id}`}
                >
                  <Link to={`/products/${product.id}`}>
                    <img
                      src={products.getImage(product.images?.[0])}
                      alt={product.title}
                      className="product-image"
                    />
                    <div className="product-info">
                      <h3 className="product-title">{product.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">by {product.artistName}</p>
                      {product.artDescription && (
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                          {product.artDescription}
                        </p>
                      )}
                      <p className="product-price">
                        {product.currency === 'USD' ? '$' : '₹'}{product.price.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        GST & shipping extra
                      </p>
                    </div>
                  </Link>
                  {/* Heart Button */}
                  <button
                    onClick={(e) => handleLike(e, product.id)}
                    className={`w-full mt-3 py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 ${
                      likedProducts.has(product.id)
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-500'
                    }`}
                  >
                    <Heart 
                      className={`w-5 h-5 ${likedProducts.has(product.id) ? 'fill-white' : ''}`} 
                    />
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-sm ${
                      likedProducts.has(product.id) 
                        ? 'bg-white/20' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {product.likes || 0}
                    </span>
                  </button>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/products" data-testid="view-all-btn">
                <Button className="btn btn-primary">View All Products</Button>
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="max-w-2xl mx-auto">
              <Palette className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-2xl font-bold mb-3 text-gray-800">No Products Yet</h3>
              <p className="text-gray-600 mb-6">
                We're just getting started! Our talented artists are preparing their beautiful artworks to showcase here.
                Check back soon or register as an artist to be the first to list your creations.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/register">
                  <Button className="btn btn-primary">Register as Artist</Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="section" style={{background: '#000000', color: '#ffffff'}}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="section-title" data-testid="cta-title" style={{color: '#ffffff'}}>Start Your Art Journey</h2>
          <p className="section-subtitle mb-6" data-testid="cta-subtitle" style={{color: '#a3a3a3'}}>
            Whether you're an art lover or a creator, join our community today
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/products" data-testid="cta-shop-btn">
              <Button className="btn btn-primary">Shop Now</Button>
            </Link>
            <Link to="/register" data-testid="cta-register-btn">
              <Button className="btn btn-secondary">Register as Artist</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;